
import { defineComponent, PropType } from 'vue'
import { FilterType } from '~/types/active3rdPartyVuln'
import { SEVERITY_MAPPINGS, REACHABILITY_MAPPINGS } from '~/utils/active3rdPartyVuln'

type VulnerabilityData = {
  slug: string
  name: string
  value: number
}

type MappingValue = {
  name: string
  order: number
}

type Mappings = Record<string, MappingValue>

export default defineComponent({
  name: 'VulnerabilityBreakdownStats',
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    loaderCount: {
      type: Number,
      default: 4
    },
    stats: {
      type: Array as PropType<VulnerabilityData[]>,
      required: true
    },
    filterType: {
      type: String as PropType<FilterType>,
      default: FilterType.SEVERITY
    }
  },
  computed: {
    severityMappings(): Mappings {
      return {
        cri: { name: SEVERITY_MAPPINGS.cri, order: 1 },
        hig: { name: SEVERITY_MAPPINGS.hig, order: 2 },
        med: { name: SEVERITY_MAPPINGS.med, order: 3 },
        low: { name: SEVERITY_MAPPINGS.low, order: 4 }
      }
    },

    reachabilityMappings(): Mappings {
      return {
        rea: { name: REACHABILITY_MAPPINGS.rea.name, order: 1 },
        unr: { name: REACHABILITY_MAPPINGS.unr.name, order: 2 },
        unk: { name: REACHABILITY_MAPPINGS.unk.name, order: 3 }
      }
    },

    currentMappings(): Mappings {
      return this.filterType === FilterType.SEVERITY
        ? this.severityMappings
        : this.reachabilityMappings
    },

    allStats(): VulnerabilityData[] {
      // Ensure stats is an array
      if (!Array.isArray(this.stats)) {
        return []
      }

      // Create a map of existing data
      const dataMap: { [key: string]: number } = this.stats.reduce(
        (acc: { [key: string]: number }, item: VulnerabilityData) => {
          acc[item.slug] = item.value || 0
          return acc
        },
        {}
      )

      // Return all mappings with actual counts or 0
      return Object.keys(this.currentMappings)
        .map((key) => ({
          slug: key,
          name: this.currentMappings[key].name,
          value: dataMap[key] || 0
        }))
        .sort((a, b) => {
          const orderA = this.currentMappings[a.slug]?.order || 999
          const orderB = this.currentMappings[b.slug]?.order || 999
          return orderA - orderB
        })
    }
  }
})
